import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { MainLayout } from './components/layouts/MainLayout';
import { AuthLayout } from './components/layouts/AuthLayout';
import { AdminLayout } from './components/layouts/AdminLayout';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { JobList } from './pages/JobList';
import { JobDetail } from './pages/JobDetail';
import { SubmitProposal } from './pages/SubmitProposal';
import { JobProposals } from './pages/JobProposals';
import { ProposalDetail } from './pages/ProposalDetail';
import { MyJobs } from './pages/MyJobs';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminUsers } from './pages/admin/AdminUsers';
import { AdminUserDetail } from './pages/admin/AdminUserDetail';
import { AdminJobs } from './pages/admin/AdminJobs';
import { AdminCategories } from './pages/admin/AdminCategories';
import { AdminReports } from './pages/admin/AdminReports';
import { AdminBanners } from './pages/admin/AdminBanners';
import { ProfessionalProfile } from './pages/worker/ProfessionalProfile';
import { Profile } from './pages/Profile';
import { Messages } from './pages/Messages';
import { ChatDetail } from './pages/ChatDetail';
import { Workers } from './pages/Workers';
import { NotFound } from './pages/NotFound';

const PrivateRoute: React.FC<{ children: React.ReactNode; allowedTypes?: ('client' | 'worker' | 'admin')[] }> = ({
  children,
  allowedTypes,
}) => {
  const { user, isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedTypes && user && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  const { fetchUser } = useAuthStore();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
    }
  }, [fetchUser]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Auth pages - sin sidebar, con header minimal */}
        <Route element={<AuthLayout><Outlet /></AuthLayout>}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
        </Route>

        {/* Admin panel - layout propio con identidad visual de la app mobile */}
        <Route element={<AdminLayout><Outlet /></AdminLayout>}>
          <Route
            path="/admin/dashboard"
            element={
              <PrivateRoute allowedTypes={['admin']}>
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PrivateRoute allowedTypes={['admin']}>
                <AdminUsers />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users/:id"
            element={
              <PrivateRoute allowedTypes={['admin']}>
                <AdminUserDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/jobs"
            element={
              <PrivateRoute allowedTypes={['admin']}>
                <AdminJobs />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/categories"
            element={
              <PrivateRoute allowedTypes={['admin']}>
                <AdminCategories />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <PrivateRoute allowedTypes={['admin']}>
                <AdminReports />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/banners"
            element={
              <PrivateRoute allowedTypes={['admin']}>
                <AdminBanners />
              </PrivateRoute>
            }
          />
        </Route>

        {/* Public + Dashboard pages - con navbar completa */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/jobs/:id" element={<JobDetail />} />
          <Route
            path="/jobs/:id/propose"
            element={
              <PrivateRoute allowedTypes={['worker']}>
                <SubmitProposal />
              </PrivateRoute>
            }
          />
          <Route
            path="/jobs/:id/proposals"
            element={
              <PrivateRoute allowedTypes={['client', 'admin']}>
                <JobProposals />
              </PrivateRoute>
            }
          />
          <Route
            path="/proposals/:id"
            element={
              <PrivateRoute>
                <ProposalDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/my-jobs"
            element={
              <PrivateRoute>
                <MyJobs />
              </PrivateRoute>
            }
          />
          <Route path="/worker/jobs" element={<JobList />} />
          <Route
            path="/workers"
            element={
              <PrivateRoute>
                <Workers />
              </PrivateRoute>
            }
          />

          <Route
            path="/worker/profile"
            element={
              <PrivateRoute allowedTypes={['worker']}>
                <ProfessionalProfile />
              </PrivateRoute>
            }
          />
          <Route path="/users/:id" element={<Profile />} />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <PrivateRoute>
                <Messages />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/:id"
            element={
              <PrivateRoute>
                <ChatDetail />
              </PrivateRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
